import React, { Suspense, useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useParams, Navigate } from 'react-router-dom'
import { SocketProvider } from './contexts/SocketContext'
import Loading from './components/Loading'
import { ToastProvider } from './components/Toast'
import { ConfirmProvider } from './components/ConfirmDialog'
import { authMe } from './utils/api'

// Single source of truth — one admin shell (AdminV2) + one voter shell
// (ConferenceRoom which renders ParticipantView). The legacy AdminRoom /
// AdminV3 / ParticipantJitsi experiments are no longer routable so the UI
// can never drift across versions.
const ConferenceRoom = React.lazy(() => import('./components/ConferenceRoom'))
const AdminV2 = React.lazy(() => import('./components/v2/AdminV2'))
const LandingPage = React.lazy(() => import('./components/LandingPage'))
const NotFoundPage = React.lazy(() => import('./components/NotFoundPage'))

function ParticipantPage() {
  const { id, user_id } = useParams()
  return (
    <SocketProvider>
      <Suspense fallback={<Loading />}>
        <ConferenceRoom conferenceId={id} userId={user_id} />
      </Suspense>
    </SocketProvider>
  )
}

// Guard for admin routes. The session JWT is now an HttpOnly cookie that JS
// can't read, so we verify the session server-side via /auth/me (the cookie
// rides along automatically). A definitive 401 redirects to login; temporary
// network/server failures stay on a reconnecting screen and retry. This keeps
// non-admins out without turning a deploy or Wi-Fi handover into a logout.
function AdminGuard ({ children }) {
  const [status, setStatus] = useState('checking') // 'checking' | 'reconnecting' | 'ok' | 'denied'
  useEffect(() => {
    let alive = true
    let retryTimer = null
    const check = async () => {
      if (!alive) return
      try {
        const admin = await authMe()
        if (alive) setStatus(admin ? 'ok' : 'denied')
      } catch {
        if (!alive) return
        setStatus('reconnecting')
        retryTimer = setTimeout(check, 2500)
      }
    }
    const retryNow = () => {
      if (retryTimer) clearTimeout(retryTimer)
      check()
    }
    check()
    window.addEventListener('online', retryNow)
    return () => {
      alive = false
      if (retryTimer) clearTimeout(retryTimer)
      window.removeEventListener('online', retryNow)
    }
  }, [])
  if (status === 'checking') return <Loading />
  if (status === 'reconnecting') return <Loading label="Verbindung wird wiederhergestellt…" />
  if (status === 'denied') return <Navigate to="/" replace />
  return children
}

const App = () => (
  <ToastProvider>
    <ConfirmProvider>
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Landing */}
          <Route path="/" element={<Suspense fallback={<Loading />}><LandingPage /></Suspense>} />

          {/* Participant — new ParticipantView via ConferenceRoom. */}
          <Route path="/conference/:id" element={<ParticipantPage />} />
          <Route path="/conference/:id/:user_id" element={<ParticipantPage />} />

          {/* Admin — AdminV2 only. Old /admin/v3 and /admin/legacy routes are
              gone; if anyone follows a stale link they land on AdminV2. */}
          <Route path="/conference/:id/admin" element={<AdminGuard><Suspense fallback={<Loading />}><AdminV2 /></Suspense></AdminGuard>} />
          <Route path="/conference/:id/admin/*" element={<AdminGuard><Suspense fallback={<Loading />}><AdminV2 /></Suspense></AdminGuard>} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
    </ConfirmProvider>
  </ToastProvider>
)

export default App
